<template>
  <div
    class="w-full v-input"
    :class="[disabled && 'disabled', error && 'error']"
    @click.stop
  >
    <div v-if="title" class="mb-1 space-x-1 text-xs">
      <span
        :class="disabled ? '!text-zinc-400' : 'text-slate-700'"
        v-text="title"
      />
      <span
        v-if="!required || hint"
        class="italic"
        :class="disabled ? '!text-zinc-400' : 'text-slate-400'"
      >-</span>
      <span
        v-if="!required"
        class="italic"
        :class="disabled ? '!text-zinc-400' : 'text-slate-400'"
      >optional</span>
      <span
        v-if="hint"
        class="italic"
        :class="disabled ? '!text-zinc-400' : 'text-slate-400'"
        v-text="hint"
      />
    </div>
    <label
      :for="`vinput_${uid}`"
      :class="[
        disabled ? 'cursor-not-allowed' : 'cursor-text',
        customClass,
        labelClass
      ]"
    >
      <input
        :id="`vinput_${uid}`"
        v-focus="focus"
        class="flex-grow min-w-0 text-sm tabular-nums leading-5 bg-white border-0 focus:ring-0 focus:outline-none text-zinc-700 no-stepper"
        :class="[inputClass, disabled && 'cursor-not-allowed pointer-events-none']"
        :type="_type"
        :value="modelValue"
        :placeholder="_placeholder"
        :disabled="disabled"
        :min="min"
        :max="max"
        :autocomplete="_autocomplete"
        step="any"
        @beforeinput="onBeforeInput"
        @input="input"
        @keydown="onKeyDown"
        @blur="onBlur"
        @keydown.down="$emit('down')"
        @keydown.up="$emit('up')"
        @keyup.enter="$emit('enter')"
      >
      <slot name="post">
        <span
          v-if="post || type === 'date'"
          class="flex-shrink-0 ml-2 text-sm leading-5 pointer-events-none select-none p-[10px] text-zinc-400"
          v-text="post || _day"
        />
      </slot>
      <template v-if="_type === 'number'">
        <button
          class="flex justify-center items-center p-2.5 rounded-lg border border-transparent focus:outline-none"
          :class="disabled ? 'cursor-not-allowed pointer-events-none text-zinc-300' : 'hover:bg-slate-100 text-zinc-600 hover:border-slate-100 active:border-primary'"
          :title="'CTRL -100 \r\nSHIFT -10 \r\nALT -0.1'"
          @click="step($event, -1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M3.33301 8H12.6663"
              stroke="currentColor"
              stroke-width="1.33333"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          class="flex justify-center items-center p-2.5 rounded-lg border border-transparent focus:outline-none"
          :class="disabled ? 'cursor-not-allowed pointer-events-none text-zinc-300' : 'hover:bg-slate-100 text-zinc-600 hover:border-slate-100 active:border-primary'"
          :title="'CTRL +100 \r\nSHIFT +10 \r\nALT +0.1'"
          @click="step($event, +1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8 3.33325V12.6666"
              stroke="currentColor"
              stroke-width="1.33333"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M3.33301 8H12.6663"
              stroke="currentColor"
              stroke-width="1.33333"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </template>
    </label>
    <p
      v-if="error && typeof error === 'string'"
      class="flex items-center mt-1 space-x-1 text-xs text-red-500"
    >
      <i class="mx-1 fal fa-times" />
      <span v-text="error" />
    </p>
  </div>
</template>

<script>
  import { debounce, focusDirective } from '@krknet/fe/helpers'
  import { formatDate } from '@/helpers'

  export default {
    // eslint-disable-next-line vue/component-definition-name-casing
    name: 'vInput',
    directives: { focus: focusDirective },
    props: {
      modelValue: { type: [String, Number], required: false },
      autocomplete: { type: String, required: false },
      customClass: { type: String, required: false },
      inputClass: { type: String, required: false },
      labelClass: { type: String, required: false },
      post: { type: String, required: false },
      title: { type: String, required: false },
      placeholder: { type: String, required: false, default: '' },
      type: { type: String, required: false, default: 'text' },
      min: { type: Number, required: false },
      max: { type: Number, required: false },
      required: { type: Boolean, required: false, default: false },
      disabled: { type: Boolean, required: false, default: false },
      focus: { type: Boolean, required: false, default: false },
      debounce: { type: [Number, Boolean], required: false, default: false },
      hint: { type: String, required: false },
      trim: { type: Boolean, required: false, default: false },
      toLowerCase: { type: Boolean, required: false, default: false },
      toUpperCase: { type: Boolean, required: false, default: false },
      error: { type: [Boolean, String], required: false, default: false }
    },
    emits: ['blur', 'down', 'up', 'enter', 'update:modelValue'],
    data () {
      return {
        uid: this.$.uid
      }
    },
    computed: {
      _autocomplete () {
        return this.autocomplete || (this._type === 'number' ? 'off' : false)
      },
      _type () {
        if (this.type === 'integer') return 'number'
        if (this.type === 'mixed') return 'string'
        if (this.type === 'date') return 'string'
        if (this.type === 'day') return 'string'
        return this.type
      },
      _placeholder () {
        if (this.placeholder) return this.placeholder

        switch (this.type) {
          case 'password':
            return '**************'
          case 'integer':
            return '0 '
          case 'number':
            return '0.00'
          case 'date':
            return formatDate()
          case 'day':
            return formatDate().slice(5)
          default:
            return this.title
        }
      },
      _day () {
        if (this.type !== 'date') return
        return ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][new Date(this.modelValue).getDay()]
      }
    },
    created () {
      this.emit = this.debounce ? debounce((...args) => this.$emit(...args), this.debounce === true || this.debounce <= 0 ? 200 : this.debounce) : this.$emit
    },
    methods: {
      step (event, direction) {
        const parser = this.type === 'integer' ? Number.parseInt : Number.parseFloat
        const currentValue = !Number.isNaN(parser(this.$el.querySelector('input').value)) ? parser(this.$el.querySelector('input').value) : (this.min || 0)
        const modifier = (isMac ? event.metaKey : event.ctrlKey) ? 100 : event.shiftKey ? 10 : event.altKey ? 0.1 : 1

        const decimals = Math.max(
          (currentValue.toString().split('.')[1] || '').length,
          event.altKey ? 1 : 0
        )

        let newValue = currentValue + (direction * modifier)
        newValue = parser(newValue.toFixed(decimals))
        newValue = Math.max(this.min === undefined ? -Infinity : this.min, Math.min(this.max === undefined ? Infinity : this.max, newValue))
        newValue = Number.isNaN(newValue) ? undefined : newValue
        this.$el.querySelector('input').value = newValue
        this.$emit('update:modelValue', newValue)
        this.$emit('blur')
      },
      onBeforeInput (event) {
        if (this._type !== 'number') return
        if (event.inputType === 'insertText' && !/[0-9.,-]/.test(event.data)) event.preventDefault()
      },
      onKeyDown (event) {
        switch (this.type) {
          case 'integer':
          case 'number':
            if (['ArrowUp', 'ArrowDown'].includes(event.key)) handleNumberSteps(event)
            break
          case 'mixed':
            if (['ArrowUp', 'ArrowDown'].includes(event.key)) handleMixedSteps(event)
            break
          case 'date':
            if (['ArrowUp', 'ArrowDown'].includes(event.key)) handleDateSteps(event)
            break
          case 'day':
            if (['ArrowUp', 'ArrowDown'].includes(event.key)) handleDaySteps(event)
            break
        }

        if (event.isHandled) {
          this.input(event)
          this.onBlur(event)
        }
      },
      setFocus () {
        this.$el.querySelector('input').focus()
      },
      onBlur (event) {
        this.$emit('blur')
        if (this.type === 'date') this.emit('update:modelValue', event.target.value)
      },
      input (event) {
        let value = event.target.value

        if (['integer', 'number'].includes(this.type)) {
          if (value === '') return this.emit('update:modelValue', undefined)

          const parser = this.type === 'integer' ? Number.parseInt : Number.parseFloat
          value = Number.isNaN(parser(value)) ? this.modelValue : parser(value)
          value = Math.max(this.min === undefined ? -Infinity : this.min, Math.min(this.max === undefined ? Infinity : this.max, value))
          value = Number.isNaN(value) ? undefined : value
        }

        if (this.type === 'text') {
          if (this.trim) value = `${value || ''}`.trim()
          if (this.toLowerCase) value = `${value || ''}`.toLowerCase()
          if (this.toUpperCase) value = `${value || ''}`.toUpperCase()
        }

        if (value.toString() !== event.target.value) event.target.value = value
        if (this.type !== 'date') this.emit('update:modelValue', value)
      }
    }
  }

  const isMac = navigator.platform === 'MacIntel'
  const currentYear = new Date().getFullYear()

  const handleNumberSteps = event => {
    event.preventDefault()
    const currentValue = Number.isNaN(Number.parseFloat(event.target.value)) ? Number.parseFloat(event.target.getAttribute('min')) || 0 : Number.parseFloat(event.target.value)
    const direction = event.key === 'ArrowUp' ? 1 : -1
    const modifier = (isMac ? event.metaKey : event.ctrlKey) ? 100 : event.shiftKey ? 10 : event.altKey ? 0.1 : 1

    const decimals = Math.max(
      (currentValue.toString().split('.')[1] || '').length,
      event.altKey ? 1 : 0
    )

    const newValue = currentValue + (direction * modifier)
    event.target.value = newValue.toFixed(decimals)
    event.isHandled = true
  }

  const handleMixedSteps = event => {
    event.preventDefault()
    const [prefix, currentValue] = resolveValue(event.target.value || 0)
    const direction = event.key === 'ArrowUp' ? 1 : -1
    const modifier = (isMac ? event.metaKey : event.ctrlKey) ? 100 : event.shiftKey ? 10 : event.altKey ? 0.1 : 1

    const decimals = Math.max(
      (currentValue.toString().split('.')[1] || '').length,
      event.altKey ? 1 : 0
    )

    const newValue = currentValue + (direction * modifier)
    event.target.value = prefix + newValue.toFixed(decimals)
    event.isHandled = true
  }

  const handleDateSteps = event => {
    event.preventDefault()
    const date = event.target.value ? new Date(event.target.value) : new Date()
    const direction = event.key === 'ArrowUp' ? 1 : -1

    if (isMac ? event.metaKey : event.ctrlKey) date.setMonth(date.getMonth() + (1 * direction))
    else if (event.shiftKey) date.setDate(date.getDate() + (7 * direction))
    else date.setDate(date.getDate() + (1 * direction))

    if (date.getDay() === 0) date.setDate(date.getDate() + (1 * direction))

    event.target.value = formatDate(date).slice(0, 10)
    event.isHandled = true
  }

  const handleDaySteps = event => {
    event.preventDefault()
    const date = event.target.value ? new Date(`${currentYear}-${event.target.value}`) : new Date()
    const direction = event.key === 'ArrowUp' ? 1 : -1

    if (isMac ? event.metaKey : event.ctrlKey) date.setMonth(date.getMonth() + (1 * direction))
    else if (event.shiftKey) date.setDate(date.getDate() + (7 * direction))
    else date.setDate(date.getDate() + (1 * direction))

    if (date.getDay() === 0) date.setDate(date.getDate() + (1 * direction))

    event.target.value = formatDate(date).slice(5, 10)
    event.isHandled = true
  }

  const numberRegex = /^-?\d+(\.\d+)?$/
  function resolveValue (value, fallback) {
    if (value.length === 0) return fallback
    if (numberRegex.test(value)) return ['', Number.parseInt(value)]

    for (let i = 1; i < value.length; i++) {
      const prefix = value.slice(0, i)
      const testValue = value.slice(i)
      if (numberRegex.test(testValue)) {
        if (prefix && testValue.startsWith('-')) return [`${prefix}-`, Number.parseInt(testValue.slice(1))]
        return [prefix, Number.parseInt(testValue)]
      }
    }

    return ['', fallback]
  }
</script>

<style>
  .v-input label {
    @apply flex overflow-hidden items-center w-full text-sm bg-white border rounded-[10px] border-gray-200;
    box-shadow: 1px 4px 4px 0px rgba(255, 255, 255, 0.25) inset, 4px 0px 8px 0px rgba(196, 196, 196, 0.10) inset, 0px 1px 1px 0px rgba(24, 24, 24, 0.05);
  }
  .v-input label:hover {
    @apply border-gray-400;
    box-shadow: 1px 4px 4px 0px rgba(255, 255, 255, 0.25) inset, 4px 0px 8px 0px rgba(196, 196, 196, 0.10) inset, 0px 1px 1px 0px rgba(24, 24, 24, 0.10);
  }
  .v-input label:active,
  .v-input label:focus-within {
    @apply border-primary;
    box-shadow: 0px 0px 6px 0px var(--primary-80, rgba(189, 224, 56, 0.80)), 1px 4px 4px 0px rgba(255, 255, 255, 0.25) inset, 4px 0px 8px 0px rgba(196, 196, 196, 0.10) inset, 0px 1px 3px 0px rgba(24, 24, 24, 0.10);
  }

  .v-input.error label {
    @apply border-red-500;
    box-shadow: 0px 0px 6px 0px var(--color-red-300, #FFA2A2), 1px 4px 4px 0px rgba(255, 255, 255, 0.25) inset, 4px 0px 8px 0px rgba(196, 196, 196, 0.10) inset, 0px 1px 3px 0px rgba(24, 24, 24, 0.10);
  }

  .v-input.disabled label {
    @apply hover:border-gray-200 focus-within:border-gray-200 active:border-gray-200;
    box-shadow: 1px 4px 4px 0px rgba(255, 255, 255, 0.25) inset, 4px 0px 8px 0px rgba(196, 196, 196, 0.10) inset, 0px 1px 1px 0px rgba(24, 24, 24, 0.05);
  }
  .v-input.disabled input {
    @apply text-zinc-300;
  }

  .v-input input {
      @apply py-2.5 px-4;
  }
  .v-input input::placeholder {
    @apply text-zinc-300 not-italic;
  }

  input[type='number'].no-stepper::-webkit-outer-spin-button,
  input[type='number'].no-stepper::-webkit-inner-spin-button {
    -moz-appearance: textfield;
    -webkit-appearance: none;
  }

  /* Firefox */
  input[type='number'].no-stepper {
    -moz-appearance: textfield;
  }
</style>
